/**
 * Today for the founder and the Ops associate director — prototype `today` (docs/prototype.html 1397):
 * greeting, "Client priority" pills, one card per client (open when it has something that needs a manager
 * today, highest priority first), "Business at a glance" and "Not about a client".
 *
 * Every row is an exception from GET /today (ADR §8.11): the server decides what counts, how bad it is and
 * where its one button goes. Revenue shows only when the API sends it (founder and AD; members never get it).
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { ClientRow, Exception, ExceptionKind, Priority } from '@amplispot/contracts';
import { useClients } from '../../lib/data';
import { useMoneyOverview } from '../../lib/money';
import { useCrossSellCounts } from '../../lib/opportunities';
import { revenueAtRiskNote } from '../../lib/revenue';
import { firstName, rupees } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useCreatedToday } from '../../lib/team';
import { useToday } from '../../lib/work';
import { ErrorState, Loading, Pills, PriorityChip } from '../../ui/kit';
import { LogoWithDot } from '../clients/parts';
import { LogMeetingModal } from '../clients/Meetings';
import { Lbl, PageInfo } from '../../shell/Help';

/** The short type label at the start of each row (prototype `.kd`). */
const KIND: Record<ExceptionKind, string> = {
  held: 'Held', commit: 'Task', esc: 'Escalation', chg: 'Change', proof: 'Proof', req: 'Request', triage: 'Request',
  blk: 'Blocked', mailesc: 'Email', sil: 'Quiet', dropped: 'Dropped', late: 'Proposed', inc: 'Incident', ts: 'Timesheet',
  usg: 'Usage', idea: 'Idea', overdue: 'Task',
};

const PRIO_ORDER: Record<Priority, number> = { high: 0, med: 1, low: 2 };

export function greeting(): string {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Asia/Kolkata' }).format(new Date()));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function ManagerToday() {
  const { me, readOnly, can } = useSession();
  const nav = useNavigate();
  const today = useToday();
  const clients = useClients();
  // Prototype xsell(c): the client's opportunities not yet closed ("N cross-sell(s)" chip on its card).
  const { counts: xsell } = useCrossSellCounts();
  const [prio, setPrio] = useState<'all' | Priority>('all');
  const [openAll, setOpenAll] = useState<Record<string, boolean>>({});
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const [logging, setLogging] = useState(false);

  const ex = today.data?.exceptions ?? [];
  const byClient = useMemo(() => {
    const m = new Map<string, Exception[]>();
    for (const e of ex) if (e.client) m.set(e.client.id, [...(m.get(e.client.id) ?? []), e]);
    return m;
  }, [ex]);
  // An exception whose client is not in the (active) client list — e.g. an archived client with a task still
  // blocked — still counts in the Today badge, so it shows here, under "Not about a client", with its name.
  const listed = useMemo(() => new Set((clients.data ?? []).map((c) => c.id)), [clients.data]);
  const noClient = ex.filter((e) => !e.client || !listed.has(e.client.id));
  const rows = useMemo(
    () =>
      [...(clients.data ?? [])].sort(
        (a, b) => PRIO_ORDER[a.priority] - PRIO_ORDER[b.priority] || (byClient.get(b.id)?.length ?? 0) - (byClient.get(a.id)?.length ?? 0) || a.name.localeCompare(b.name),
      ),
    [clients.data, byClient],
  );
  const needing = rows.filter((c) => byClient.has(c.id));
  const highNeeding = needing.filter((c) => c.priority === 'high').length;
  const count = (p: Priority) => rows.filter((c) => c.priority === p).length;
  const shown = prio === 'all' ? rows : rows.filter((c) => c.priority === prio);

  if (today.isLoading || clients.isLoading) return <Loading />;
  if (today.error) return <ErrorState error={today.error} onRetry={() => void today.refetch()} />;
  if (clients.error) return <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />;
  const t = today.data!;
  const go = (route: string) => nav(route);

  return (
    <>
      <div className="ph">
        <div>
          <h1>
            {greeting()}, {firstName(me.user.name)}
            <PageInfo helpKey="today" />
          </h1>
          <p>
            {needing.length
              ? `${needing.length} ${needing.length === 1 ? 'client needs' : 'clients need'} attention today, ${highNeeding} of them high priority. Highest priority first.`
              : noClient.length
                ? 'No client needs you today. A few things inside the team do.'
                : 'Nothing needs you today.'}
          </p>
        </div>
        <div className="r">
          {/* Prototype head(…, 'Log a meeting') → newMeet(): the minutes' actions become tasks for the SPOC. */}
          <button className="btn" onClick={() => setLogging(true)} disabled={readOnly} title={readOnly ? 'Read-only while you view as someone' : undefined}>
            Log a meeting
          </button>
        </div>
      </div>

      {can('team.read') ? <CreatedTodayStrip /> : null}

      <div className="bar" style={{ marginTop: 0 }}>
        <span className="lbl">Client priority</span>
        <Pills<'all' | Priority>
          options={[
            ['all', 'All', rows.length],
            ['high', 'High', count('high')],
            ['med', 'Medium', count('med')],
            ['low', 'Low', count('low')],
          ]}
          value={prio}
          onChange={setPrio}
        />
      </div>

      <div className="cols c21">
        <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
          {shown.map((c) => {
            const items = byClient.get(c.id) ?? [];
            const open = items.length > 0 && !shut[c.id];
            const urgent = items.filter((e) => e.band === 'at_risk').length;
            const xs = xsell.get(c.id) ?? 0;
            const list = openAll[c.id] ? items : items.slice(0, 4);
            return (
              <div key={c.id} className={`ccard p-${c.priority}${open ? ' open' : ''}`}>
                <div
                  className="chd"
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  onClick={() => items.length && setShut((s) => ({ ...s, [c.id]: !s[c.id] }))}
                  onKeyDown={(e) => e.key === 'Enter' && items.length && setShut((s) => ({ ...s, [c.id]: !s[c.id] }))}
                >
                  <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                  <LogoWithDot id={c.id} name={c.name} band={c.band} />
                  <span className="nm">{c.name}</span>
                  <PriorityChip p={c.priority} />
                  {xs ? (
                    <span className="chip hot" title="Cross-sell work in progress">
                      {xs} cross-sell{xs > 1 ? 's' : ''}
                    </span>
                  ) : null}
                  {c.annual_revenue_lakh != null ? <span className="rv">{rupees(c.annual_revenue_lakh)} / yr</span> : null}
                  <span className="sub" style={{ fontSize: 12 }}>
                    SPOC {firstName(c.spoc.name)}
                  </span>
                  <div className="grow" />
                  {urgent ? <span className="chip cold">{urgent} urgent</span> : null}
                  <ClientCounts things={items.length} />
                  <button
                    className="btn sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      go(`/clients/book?client=${c.id}`);
                    }}
                  >
                    Open client
                  </button>
                </div>
                {open ? (
                  <>
                    {list.map((e) => (
                      <ExRow key={e.key} e={e} onGo={() => go(e.route)} />
                    ))}
                    {items.length > 4 && !openAll[c.id] ? (
                      <div
                        className="it"
                        role="button"
                        tabIndex={0}
                        style={{ justifyContent: 'center', color: 'var(--brand)', fontSize: 12.5, fontWeight: 600 }}
                        onClick={() => setOpenAll((s) => ({ ...s, [c.id]: true }))}
                      >
                        Show {items.length - 4} more
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
          {!shown.length ? (
            <div className="panel">
              <div className="empty">
                <b>No {prio === 'med' ? 'medium' : prio} priority clients</b>Switch the filter above.
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <Glance t={t} clients={rows} />
          <div className="panel">
            <div className="hd">
              <h3><Lbl>Not about a client</Lbl></h3>
            </div>
            {noClient.length ? (
              noClient.map((e) => (
                <div key={e.key} className="li click" role="button" tabIndex={0} onClick={() => go(e.route)} onKeyDown={(k) => k.key === 'Enter' && go(e.route)}>
                  <div className="b">
                    <div className="t">{e.title}</div>
                    <div className="m">
                      {e.client ? <span>{e.client.name}</span> : null}
                      <span>{e.reason}</span>
                    </div>
                  </div>
                  <button className="btn sm">{e.action_label}</button>
                </div>
              ))
            ) : (
              <div className="empty">
                <b>Nothing waiting</b>No change requests, proofs or dropped items need you.
              </div>
            )}
          </div>
        </div>
      </div>
      {logging ? <LogMeetingModal onClose={() => setLogging(false)} /> : null}
    </>
  );
}

/** "4 things today" when something needs a manager; otherwise the client's open and late work. */
/** Prototype ccard `.chd` for a manager: "{n} things today" on every card (the urgent chip sits before it). */
function ClientCounts({ things }: { things: number }) {
  return <span className="chip">{things} {things === 1 ? 'thing' : 'things'} today</span>;
}

function ExRow({ e, onGo }: { e: Exception; onGo: () => void }) {
  return (
    <div className="it" role="button" tabIndex={0} onClick={onGo} onKeyDown={(k) => k.key === 'Enter' && onGo()}>
      <span className="kd">{KIND[e.kind]}</span>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="tt">{e.title}</div>
        <div className="mm">{e.reason}</div>
      </div>
      <span className={`chip ${e.band === 'at_risk' ? 'cold' : 'warm'}`}>{e.action_label}</span>
    </div>
  );
}

function Glance({ t, clients }: { t: NonNullable<ReturnType<typeof useToday>['data']>; clients: ClientRow[] }) {
  const high = clients.filter((c) => c.priority === 'high');
  const highAtRisk = high.filter((c) => c.band === 'at_risk').length;
  const rar = t.tiles.revenue_at_risk_lakh;
  // On the Zoho branch the total is on the numerator's basis: 12 x total monthly revenue (GET /money/overview).
  const { can } = useSession();
  const zoho = rar !== undefined && t.tiles.revenue_source === 'zoho';
  const money = useMoneyOverview(zoho && can('money.read'));
  const note =
    rar === undefined
      ? ''
      : revenueAtRiskNote({
          rar,
          source: t.tiles.revenue_source,
          founderAnnualTotal: clients.reduce((a, c) => a + (c.annual_revenue_lakh ?? 0), 0),
          zohoMonthlyTotal: money.data?.kpis.total_mrr_lakh,
        });
  const tone = (n: number) => ({ fontSize: 19, color: n ? 'var(--cold)' : 'var(--ink)' });
  // Prototype glance('Client tasks overdue', COMMIT open & past due): promises made to a client (client_tasks), not
  // internal tasks. GET /today sends one `commit` exception for each open client task past its due date; the row opens
  // the same set (Tasks narrowed to client tasks, overdue) so the count and the list agree.
  const clientOverdue = t.exceptions.filter((e) => e.kind === 'commit').length;
  return (
    <div className="panel">
      <div className="hd">
        <h3><Lbl>Business at a glance</Lbl></h3>
      </div>
      <Link className="li click" to="/clients/book?h=at_risk" style={{ alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
        <div className="b" style={{ fontSize: 13.5 }}>
          High priority clients at risk
        </div>
        <b className="num" style={tone(highAtRisk)}>
          {highAtRisk} of {high.length}
        </b>
      </Link>
      {rar !== undefined ? (
        <div className="li" style={{ alignItems: 'center' }}>
          <div className="b" style={{ fontSize: 13.5 }}>
            Revenue at risk
            <div className="m">
              <span>{note}</span>
            </div>
          </div>
          <b className="num" style={tone(rar)}>
            {rupees(rar)}
          </b>
        </div>
      ) : null}
      <Link className="li click" to="/work/allwork?type=task&kind=client_task&status=overdue" style={{ alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
        <div className="b" style={{ fontSize: 13.5 }}>
          Client tasks overdue
        </div>
        <b className="num" style={tone(clientOverdue)}>
          {clientOverdue}
        </b>
      </Link>
    </div>
  );
}

/**
 * Prototype `VIEWS.today.render` wrapper (docs/prototype.html ~2633, managers only): "N tasks created today, M from
 * emails (K waiting for a SPOC to confirm). See all" between the greeting and the priority pills, shown even at 0.
 * Counts are Team → Created today's own (GET /team/created-today, its todayRows minus left-out): every row that is
 * not left out (tasks made today + pending proposals), those from email, and the proposals still waiting.
 * Nothing renders until it loads, and a failed load hides the strip rather than the Today screen.
 */
export function CreatedTodayStrip() {
  const q = useCreatedToday();
  if (!q.data) return null;
  const made = q.data.rows.filter((r) => r.kind !== 'left_out');
  const fromEmail = made.filter((r) => r.source === 'email').length;
  const pend = q.data.totals.waiting_spoc;
  return (
    <div className="note" style={{ margin: '0 0 14px' }} data-testid="created-today-strip">
      <b>
        {made.length} task{made.length === 1 ? '' : 's'} created today
      </b>
      , {fromEmail} from emails{pend ? ` (${pend} waiting for a SPOC to confirm)` : ''}.{' '}
      <Link to="/team/newtoday" style={{ color: 'var(--brand)', fontWeight: 600 }}>
        See all
      </Link>
    </div>
  );
}
